import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	addSharePointBookmark,
	addWebDavBookmark,
	BookmarkModel,
	SharePointBookmarkCreationModel,
	WebDavBookmarkCreationModel
} from "client/Api/Documents/Bookmarks/bookmarks";
import { BookmarkEvent, publish } from "client_legacy/Documents/Bookmarks/events";
import { DocumentItemViewModel, DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { DocumentStoreType } from "client/Api/Documents/common";
import { getFileNameWithoutExtension } from "client_legacy/Documents/path";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";

export type AddBookmarkDialogProps = {
	itemToBookmark: ItemToBookmark;
	onClose: () => void;
};

export const AddBookmarkDialog = (props: AddBookmarkDialogProps): ReactElement => {
	const {
		itemToBookmark,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>(
		() => {
			return {
				name: extractBookmarkNameFromItem(itemToBookmark)
			};
		}
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validation
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			if (!formHandler.validation.isValid()) {
				setIsBusy(false);
				return;
			}

			const { name } = formHandler.reducer
				.value;

			let promise: Promise<BookmarkModel>;
			switch (itemToBookmark.store) {
				case DocumentStoreType.SharePoint:
					{
						const { item: document } = itemToBookmark;

						const model: SharePointBookmarkCreationModel = {
							name: name,
							location: convertToLocationModel(document.location),
							itemId: document.id,
						};

						promise = addSharePointBookmark(
							model,
							globalCancellation
						);
						break;
					}
				case DocumentStoreType.WebDav:
					{
						const { serverId, item: document } = itemToBookmark;
						const fileId: string = document.id;

						const model: WebDavBookmarkCreationModel = {
							name: name,
							serverId: serverId,
							fileId: fileId
						};

						promise = addWebDavBookmark(
							model,
							globalCancellation
						);
						break;
					}
				default:
					never(itemToBookmark);
			}

			try {
				const bookmark: BookmarkModel = await promise;

				alertManager.showSuccess(
					localize("_BookmarkAddedForItemName")
						.replace(
							"{ItemName}",
							itemToBookmark.item
								.name
						)
				);

				publish(BookmarkEvent.Added, bookmark);
				onClose();

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler.reducer.value,
			formHandler.validation,
			globalCancellation,
			isBusy,
			itemToBookmark,
			onClose
		]
	);

	return (
		<Dialog hasBusyIndicator={isBusy} trackingId="add-bookmark-dialog">
			<Dialog.Header
				title={localize("_AddBookmark")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_BookmarkName")}>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
							autoFocus="select"
						/>
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Add")}
						onClick={submitHandler}
						trackingId="add"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AddBookmarkDialog.displayName = "AddBookmarkDialog";

export type SharePointItem = {
	store: DocumentStoreType.SharePoint;
	item: DocumentItemViewModel;
};

export type WebDavItem = {
	store: DocumentStoreType.WebDav;
	serverId: number;
	item: FileSystemItemModel;
};

export type ItemToBookmark = SharePointItem | WebDavItem;

type FormData = {
	name: string;
};

const validation = createValidationConfiguration<FormData>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

function extractBookmarkNameFromItem(item: ItemToBookmark): string {
	let isFile: boolean = false;
	switch (item.store) {
		case DocumentStoreType.SharePoint:
			isFile = item.item.itemType === DocumentItemType.File;
			break;
		case DocumentStoreType.WebDav:
			isFile = !item.item.isFolder;
			break;
		default:
			never(item);
	}

	let result: string = item.item
		.name;

	if (isFile) {
		result = getFileNameWithoutExtension(result);
	}

	return result;
}

export function useAddBookmarkDialog(): [
	dialogElement: ReactElement | null,
	open: (item: ItemToBookmark) => void
] {
	const [item, setItem] = useState<ItemToBookmark | null>(null);

	const renderDialog = useCallback(
		(): ReactElement | null => {
			if (item) {
				return (
					<AddBookmarkDialog
						itemToBookmark={item}
						onClose={() => setItem(null)}
					/>
				);
			}

			return null;
		},
		[item]
	);

	const openHandler = useCallback(
		(item: ItemToBookmark): void => setItem(item),
		[]
	);

	return [
		renderDialog(),
		openHandler
	];
}
